/**
 * Metrics Card Component
 * Display key statistics with large monospace numbers and animations
 */

import React, { useEffect, useState } from 'react';
import { Box, Typography, styled, keyframes } from '@mui/material';
import { Card } from '../ui/Card';
import { colors } from '../../theme/colors';
import { dataTypography } from '../../theme/typography';

interface MetricsCardProps {
  label: string;
  value: number;
  icon?: React.ReactNode;
  trend?: 'up' | 'down' | 'neutral';
  trendValue?: string;
  color?: 'primary' | 'success' | 'warning' | 'error';
  animationDelay?: number;
}

const fadeIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const AnimatedCard = styled(Card)<{ delay?: number }>(({ delay = 0 }) => ({
  animation: `${fadeIn} 0.6s ease-out ${delay}ms forwards`,
  opacity: 0,
}));

const IconContainer = styled(Box)<{ cardColor?: string }>(({ cardColor }) => {
  let backgroundColor: string;
  let iconColor: string;

  switch (cardColor) {
    case 'success':
      backgroundColor = 'rgba(32, 227, 178, 0.12)';
      iconColor = colors.success.main;
      break;
    case 'warning':
      backgroundColor = 'rgba(255, 184, 0, 0.12)';
      iconColor = colors.warning.main;
      break;
    case 'error':
      backgroundColor = 'rgba(255, 107, 107, 0.12)';
      iconColor = colors.error.main;
      break;
    default:
      backgroundColor = colors.neutral[100];
      iconColor = colors.primary.main;
  }

  return {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: 48,
    height: 48,
    borderRadius: 12,
    backgroundColor,
    color: iconColor,
    flexShrink: 0,
    '& svg': {
      fontSize: '1.5rem',
    },
  };
});

// Animated counter hook
const useAnimatedCounter = (end: number, duration: number = 1000, delay: number = 0) => {
  const [count, setCount] = useState(0);

  useEffect(() => {
    const timeout = setTimeout(() => {
      let startTime: number | null = null;
      const start = 0;

      const animate = (currentTime: number) => {
        if (!startTime) startTime = currentTime;
        const progress = Math.min((currentTime - startTime) / duration, 1);

        setCount(Math.floor(progress * (end - start) + start));

        if (progress < 1) {
          requestAnimationFrame(animate);
        }
      };

      requestAnimationFrame(animate);
    }, delay);

    return () => clearTimeout(timeout);
  }, [end, duration, delay]);

  return count;
};

export const MetricsCard: React.FC<MetricsCardProps> = ({
  label,
  value,
  icon,
  trend,
  trendValue,
  color = 'primary',
  animationDelay = 0,
}) => {
  const animatedValue = useAnimatedCounter(value, 1200, animationDelay);

  return (
    <AnimatedCard variant="default" hover delay={animationDelay}>
      <Box sx={{ p: 4 }}>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 3 }}>
          <Typography
            sx={{
              fontSize: '0.9375rem',
              fontWeight: 500,
              color: colors.text.secondary,
              letterSpacing: '0.01em',
            }}
          >
            {label}
          </Typography>
          {icon && <IconContainer cardColor={color}>{icon}</IconContainer>}
        </Box>

        <Typography
          sx={{
            fontFamily: '"JetBrains Mono", monospace',
            fontSize: '2.5rem',
            fontWeight: 700,
            color: colors.text.primary,
            lineHeight: 1,
            mb: trend ? 2 : 0,
          }}
        >
          {animatedValue.toLocaleString()}
        </Typography>

        {trend && trendValue && (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 0.5,
            }}
          >
            <Typography
              sx={{
                fontSize: '0.875rem',
                fontWeight: 500,
                color:
                  trend === 'up'
                    ? colors.success.main
                    : trend === 'down'
                      ? colors.error.main
                      : colors.text.secondary,
              }}
            >
              {trend === 'up' ? '↑' : trend === 'down' ? '↓' : '→'} {trendValue}
            </Typography>
          </Box>
        )}
      </Box>
    </AnimatedCard>
  );
};
